import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Languages, Music, Users, CalendarDays, Guitar } from "lucide-react";
import api from "@/lib/api";
import { EventCard } from "@/components/home/EventsSection";
import AIContentBlock from "@/components/AIContentBlock";
import PronounceButton from "@/components/PronounceButton";
import useTranslated from "@/hooks/useTranslated";

const Section = ({ icon: Icon, title, children, testId, aiKey, aiTopic }) => (
  <section className="mt-12" data-testid={testId}>
    <h2 className="font-display text-2xl font-black text-slate-900 flex items-center gap-2 mb-5">
      <span className="w-9 h-9 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center"><Icon className="w-4.5 h-4.5" /></span>
      {title}
    </h2>
    {aiKey && <AIContentBlock pageKey={aiKey} topic={aiTopic} compact />}
    {children}
  </section>
);

export default function PlaceDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const descT = useTranslated(`place-desc-${slug}`, data ? [data.country.description] : []);

  useEffect(() => {
    setData(null);
    api.get(`/countries/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="py-24 text-center text-slate-500" data-testid="place-not-found">Place not found. <Link to="/explore" className="text-pink-600 font-bold">Browse the world</Link></div>;
  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { country, artists, genres, instruments, events } = data;

  return (
    <div data-testid={`place-page-${slug}`}>
      <div className="relative h-64 md:h-80">
        <img src={country.image} alt={country.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pink-300 mb-2">{country.continent}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white flex items-center gap-3 flex-wrap">{country.flag} {country.name} <PronounceButton text={country.name} /></h1>
          <p className="mt-2 text-slate-200 text-sm flex items-center gap-2 flex-wrap">
            <Languages className="w-4 h-4" /> {country.languages?.join(" · ")}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-slate-600 max-w-3xl leading-relaxed" data-testid="place-description">{descT[0] || country.description}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {country.featured_genres?.map((g) => (
            <span key={g} className="px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 text-xs font-bold">{g}</span>
          ))}
        </div>

        <AIContentBlock pageKey={`place-${slug}`} topic={`The music of ${country.name} — traditions, artists and scenes`} imgQuery={`${country.name} traditional music culture`} />

        <Section icon={MapPin} title="States, Provinces & Cities" testId="place-regions-section" aiKey={`place-${slug}-regions`} aiTopic={`The regional music geography of ${country.name} — how its states and cities each carry distinct sounds`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {country.states?.map((s) => (
              <div key={s.slug} className="bg-white rounded-2xl border border-slate-100 p-5 card-lift" data-testid={`region-card-${s.slug}`}>
                <h3 className="font-bold text-slate-900">{s.name}</h3>
                <div className="mt-3 space-y-3">
                  {s.cities.map((city) => (
                    <Link key={city.name} to={`/places/${slug}/cities/${city.slug}`} data-testid={`city-link-${city.slug}`} className="flex gap-3 group rounded-xl -mx-2 px-2 py-1.5 hover:bg-pink-50/60 transition-colors">
                      <MapPin className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-slate-800 group-hover:text-pink-600 transition-colors">{city.name} ›</p>
                        <p className="text-xs text-slate-500">{city.blurb}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {artists.length > 0 && (
          <Section icon={Users} title="Artists & Creators" testId="place-artists-section" aiKey={`place-${slug}-artists`} aiTopic={`The artists and creators shaping the music of ${country.name}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {artists.map((a) => (
                <Link key={a.slug} to={`/artists/${a.slug}`} data-testid={`place-artist-${a.slug}`} className="card-lift bg-white rounded-2xl border border-slate-100 overflow-hidden group">
                  <div className="h-36 overflow-hidden"><img src={a.image} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-3">
                    <p className="font-bold text-sm text-slate-900">{a.name}</p>
                    <p className="text-xs text-slate-500 line-clamp-1">{a.roles?.join(" · ")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {genres.length > 0 && (
          <Section icon={Music} title="Genres & Traditions" testId="place-genres-section" aiKey={`place-${slug}-genres`} aiTopic={`The genres and living music traditions of ${country.name}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {genres.map((g) => (
                <Link key={g.slug} to={`/genres/${g.slug}`} className="card-lift bg-white rounded-2xl border border-slate-100 p-4">
                  <p className="font-bold text-sm text-slate-900">{g.name}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{g.description}</p>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {instruments.length > 0 && (
          <Section icon={Guitar} title="Instruments" testId="place-instruments-section" aiKey={`place-${slug}-instruments`} aiTopic={`The traditional instruments of ${country.name} and their makers`}>
            <div className="flex flex-wrap gap-3">
              {instruments.map((i) => (
                <Link key={i.slug} to={`/instruments/${i.slug}`} className="px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold hover:bg-emerald-100 transition-colors">
                  {i.name}
                </Link>
              ))}
            </div>
          </Section>
        )}

        {events.length > 0 && (
          <Section icon={CalendarDays} title="Festivals & Events" testId="place-events-section" aiKey={`place-${slug}-events`} aiTopic={`The festival and live music calendar of ${country.name}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {events.map((ev) => <EventCard key={ev.id} ev={ev} />)}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
